import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import Loader from "../../components/Loader/Loader";
import MultiSelect from "../../components/Inputs/MultiSelect";
import Select from "../../components/Inputs/Select";
import { listAdminGroups, addGroupMember, removeGroupMember } from "../../api/adminGroups";
import UserAuthStore from "../../store/userStore";
import {
  getAdminClient,
  updateAdminClient,
  revokeAdminClient,
  enableAdminClient,
  deleteAdminClient,
  rotateAdminClient,
  listAdminClientSubjects,
  tombstoneAdminClientSubject,
  decommissionAdminClientDevice,
  listAdminClientInvites,
  createAdminClientInvite,
  deleteAdminClientInvite,
} from "../../api/adminClients";
import { AuthError } from "../../api";
import ROUTES from "../../router/routes";
import useFeedback from "../../hooks/useFeedback";
import { isDirty } from "../../utils/isDirty";

// ─── Status badge ─────────────────────────────────────────────────────────────
// ─── Tag pill (matches TileCard style) ───────────────────────────────────────
const TagPill = ({ label }) => (
  <span className="photon-metadata-label inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-fill-indigo-100 text-ink-indigo-800 dark:bg-fill-hex-312e81/60 dark:text-ink-hex-a5b4fc">
    {label}
  </span>
);

// ─── Field row ────────────────────────────────────────────────────────────────
const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-0.5">{label}</p>
    <p className="text-sm text-ink-ink dark:text-ink-gray-200 break-all">{value || "—"}</p>
  </div>
);

// ─── Inline editable field ────────────────────────────────────────────────────
const EditField = ({ label, value, onChange, multiline = false, placeholder = "" }) => (
  <div>
    <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-0.5">{label}</p>
    {multiline ? (
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="photon-field w-full text-sm rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-primary/50 resize-none"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="photon-field w-full text-sm rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-primary/50"
      />
    )}
  </div>
);

// ─── Available scopes ─────────────────────────────────────────────────────────
const AVAILABLE_SCOPES = [
  { value: "llm.chat",             label: "llm.chat",             description: "Chat completions — access to /ai/chat with subject routing" },
  { value: "adk.agent.invoke",     label: "adk.agent.invoke",     description: "ADK agent invocation — access to /ai/agent" },
  { value: "media.image.generate", label: "media.image.generate", description: "Image generation — access to /ai/media/image/generate" },
  { value: "file.parse",           label: "file.parse",           description: "File text extraction — access to /files/parse" },
];

// ─── Chat platform labels ──────────────────────────────────────────────────────
const CHAT_PLATFORM_LABELS = {
  slack:       "Slack",
  teams:       "Microsoft Teams",
  google_chat: "Google Chat",
  webex:       "Cisco Webex",
  whatsapp:    "WhatsApp",
  telegram:    "Telegram",
  jabber:      "Jabber / XMPP",
};

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const BASE_TABS = ["Overview", "Config", "Credentials", "Subjects"];

const getInviteDisplayStatus = (invite) => {
  if (invite.status !== "pending" || !invite.expires_at) return invite.status;
  return new Date(invite.expires_at) <= new Date() ? "expired" : "pending";
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AppDetailPage = () => {
  const { showFeedback } = useFeedback();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Overview");
  const [revoking, setRevoking] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [newSecret, setNewSecret] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [subjects, setSubjects] = useState(null);
  const [tombstoning, setTombstoning] = useState(null);
  const [decommissioning, setDecommissioning] = useState(null); // link_id of the device being decommissioned
  const [decommissionConfirm, setDecommissionConfirm] = useState(null); // link_id when confirm UI is open
  const [decommissionReason, setDecommissionReason] = useState("admin_action");
  const [decommissionPurge, setDecommissionPurge] = useState(false);
  const [invites, setInvites] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [revokingInvite, setRevokingInvite] = useState(null);

  // ── Group assignment (Subjects tab) ─────────────────────────────────────────
  const orgId = UserAuthStore((state) => state.orgId);
  const [allGroups, setAllGroups] = useState(null);
  const [subjectAddOpen, setSubjectAddOpen] = useState(null); // link_id
  const [subjectAddQuery, setSubjectAddQuery] = useState("");
  const [subjectGroupAdding, setSubjectGroupAdding] = useState(null); // { link_id, group_id }
  const [subjectGroupRemoving, setSubjectGroupRemoving] = useState(null); // { link_id, group_id }

  // ── Edit state ───────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null); // populated when edit starts
  // Snapshot of editForm at the moment editing started, used to gate Save on
  // actual changes (webhook_signing_secret is intentionally write-only/blank
  // here, so typing a new value correctly reads as a change against "").
  const [originalEditForm, setOriginalEditForm] = useState(null);

  const fetchClient = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminClient(clientId);
      setClient(data);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load app details.", "error");
    } finally {
      setLoading(false);
    }
  }, [clientId, showFeedback]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const fetchSubjects = useCallback(async () => {
    try {
      const data = await listAdminClientSubjects(clientId);
      setSubjects(data);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load subjects.", "error");
    }
  }, [clientId, showFeedback]);

  useEffect(() => {
    if (activeTab === "Subjects" && subjects === null) {
      fetchSubjects();
    }
  }, [activeTab, subjects, fetchSubjects]);

  useEffect(() => {
    if ((activeTab === "Subjects" || editing) && allGroups === null && orgId) {
      listAdminGroups(orgId).then(setAllGroups).catch(() => setAllGroups([]));
    }
  }, [activeTab, editing, allGroups, orgId]);

  // Close add-group dropdown on outside click
  useEffect(() => {
    if (!subjectAddOpen) return;
    const handler = (e) => {
      if (!e.target.closest("[data-subject-add-dropdown]")) {
        setSubjectAddOpen(null);
        setSubjectAddQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [subjectAddOpen]);

  const fetchInvites = useCallback(async () => {
    try {
      const data = await listAdminClientInvites(clientId);
      setInvites(data);
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load invites.", "error");
    }
  }, [clientId, showFeedback]);

  useEffect(() => {
    if (activeTab === "Invites" && invites === null) {
      fetchInvites();
    }
  }, [activeTab, invites, fetchInvites]);

  const handleCreateInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      showFeedback("Enter a valid email address.", "error");
      return;
    }
    setCreatingInvite(true);
    try {
      const result = await createAdminClientInvite(clientId, email);
      showFeedback(`Invite sent to ${result.email}.`, "success");
      setInviteEmail("");
      setInvites((prev) => (prev ? [{ ...result, invite_id: result.invite_id }, ...prev] : [result]));
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "external_subjects_not_enabled") {
        showFeedback("External subjects must be enabled on this app.", "error");
      } else {
        showFeedback("Failed to create invite.", "error");
      }
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    setRevokingInvite(inviteId);
    try {
      await deleteAdminClientInvite(clientId, inviteId);
      showFeedback("Invite revoked.", "success");
      setInvites((prev) => prev.filter((i) => i.invite_id !== inviteId));
    } catch {
      showFeedback("Failed to revoke invite.", "error");
    } finally {
      setRevokingInvite(null);
    }
  };

  const handleTombstone = async (linkId) => {
    setTombstoning(linkId);
    try {
      await tombstoneAdminClientSubject(clientId, linkId);
      showFeedback("Subject tombstoned.", "success");
      setSubjects((prev) => prev.map((s) => s.link_id === linkId ? { ...s, status: "tombstoned" } : s));
    } catch {
      showFeedback("Failed to tombstone subject.", "error");
    } finally {
      setTombstoning(null);
    }
  };

  // device_id is the client_subject_id (extracted from link_id as the part after the first ':')
  const handleDecommission = async (linkId) => {
    setDecommissioning(linkId);
    try {
      const deviceId = linkId.split(":").slice(1).join(":");
      await decommissionAdminClientDevice(clientId, deviceId, { reason: decommissionReason, purge_content: decommissionPurge });
      showFeedback("Device decommissioned.", "success");
      setSubjects((prev) => prev.map((s) => s.link_id === linkId ? { ...s, status: "tombstoned" } : s));
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "subject_link_already_tombstoned") {
        showFeedback("Device is already decommissioned.", "error");
        setSubjects((prev) => prev.map((s) => s.link_id === linkId ? { ...s, status: "tombstoned" } : s));
      } else {
        showFeedback("Failed to decommission device.", "error");
      }
    } finally {
      setDecommissioning(null);
      setDecommissionConfirm(null);
      setDecommissionReason("admin_action");
      setDecommissionPurge(false);
    }
  };

  const handleSubjectGroupAdd = async (linkId, groupId, photonUserId) => {
    setSubjectGroupAdding({ link_id: linkId, group_id: groupId });
    setSubjectAddOpen(null);
    setSubjectAddQuery("");
    try {
      await addGroupMember(groupId, photonUserId);
      showFeedback("Subject added to group.", "success");
      setSubjects((prev) =>
        prev.map((s) =>
          s.link_id === linkId
            ? { ...s, group_assignments: [...(s.group_assignments || []), groupId] }
            : s
        )
      );
    } catch {
      showFeedback("Failed to add subject to group.", "error");
    } finally {
      setSubjectGroupAdding(null);
    }
  };

  const handleSubjectGroupRemove = async (linkId, groupId, photonUserId) => {
    setSubjectGroupRemoving({ link_id: linkId, group_id: groupId });
    try {
      await removeGroupMember(groupId, photonUserId);
      showFeedback("Removed from group.", "success");
      setSubjects((prev) =>
        prev.map((s) =>
          s.link_id === linkId
            ? { ...s, group_assignments: (s.group_assignments || []).filter((g) => g !== groupId) }
            : s
        )
      );
    } catch {
      showFeedback("Failed to remove subject from group.", "error");
    } finally {
      setSubjectGroupRemoving(null);
    }
  };

  const startEdit = () => {
    const loadedForm = {
      name: client.name || "",
      description: client.description || "",
      owner_team: client.owner_team || "",
      owner_contact: client.owner_contact || "",
      docs_url: client.docs_url || "",
      scopes: client.policy_prism?.scopes || [],
      allowed_subject_domains: (client.policy_prism?.allowed_subject_domains || []).join(", "),
      allow_external_subjects: client.policy_prism?.allow_external_subjects || false,
      allowed_channels: (client.policy_prism?.allowed_channels || []).join(", "),
      allowed_workspaces: (client.policy_prism?.allowed_workspaces || []).join(", "),
      webhook_signing_secret: "",
      teams_bot_app_id: client.policy_prism?.teams_bot_app_id || "",
      default_group_id: client.default_group_id || "",
    };
    setEditForm(loadedForm);
    setOriginalEditForm(loadedForm);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm(null);
    setOriginalEditForm(null);
  };

  const handleSave = async () => {
    if (!editForm.name.trim() || !editForm.description.trim() || editForm.scopes.length === 0) {
      showFeedback("Name, description and at least one scope are required.", "error");
      return;
    }
    if (client.chat_platform === "teams" && !editForm.teams_bot_app_id.trim()) {
      showFeedback("Teams Bot App ID is required.", "error");
      return;
    }
    setSaving(true);
    try {
      const splitCsv = (val) => val.trim() ? val.split(",").map((v) => v.trim()).filter(Boolean) : [];
      const domains = splitCsv(editForm.allowed_subject_domains);
      const isChat = client.client_type === "chat";
      const showSigningSecret = isChat && ["bot_token", "webhook_signature"].includes(client.client_auth_method);
      await updateAdminClient(clientId, {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        ...(editForm.owner_team.trim() && { owner_team: editForm.owner_team.trim() }),
        ...(editForm.owner_contact.trim() && { owner_contact: editForm.owner_contact.trim() }),
        ...(editForm.docs_url.trim() && { docs_url: editForm.docs_url.trim() }),
        default_group_id: editForm.default_group_id || null,
        policy_prism: {
          ...client.policy_prism,
          scopes: editForm.scopes,
          allowed_subject_domains: domains,
          allow_external_subjects: editForm.allow_external_subjects,
          ...(isChat && {
            allowed_channels:   splitCsv(editForm.allowed_channels).length   ? splitCsv(editForm.allowed_channels)   : null,
            allowed_workspaces: splitCsv(editForm.allowed_workspaces).length ? splitCsv(editForm.allowed_workspaces) : null,
          }),
          ...(showSigningSecret && editForm.webhook_signing_secret.trim() && {
            webhook_signing_secret: editForm.webhook_signing_secret.trim(),
          }),
          ...(client.chat_platform === "teams" && {
            teams_bot_app_id: editForm.teams_bot_app_id.trim(),
          }),
        },
      });
      showFeedback("App updated.", "success");
      setEditing(false);
      setEditForm(null);
      fetchClient();
    } catch {
      showFeedback("Failed to save changes.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {    setRevoking(true);
    try {
      await revokeAdminClient(clientId);
      showFeedback("App revoked.", "success");
      fetchClient();
    } catch {
      showFeedback("Failed to revoke app.", "error");
    } finally {
      setRevoking(false);
      setConfirmRevoke(false);
    }
  };

  const handleEnable = async () => {
    setEnabling(true);
    try {
      await enableAdminClient(clientId);
      showFeedback("App re-enabled.", "success");
      fetchClient();
    } catch {
      showFeedback("Failed to enable app.", "error");
    } finally {
      setEnabling(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAdminClient(clientId);
      showFeedback("App permanently deleted.", "success");
      navigate(ROUTES.SETTINGS.APPS);
    } catch {
      showFeedback("Failed to delete app.", "error");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      const result = await rotateAdminClient(clientId);
      setNewSecret(result.client_secret);
      showFeedback("Credentials rotated.", "success");
      fetchClient();
    } catch {
      showFeedback("Failed to rotate credentials.", "error");
    } finally {
      setRotating(false);
    }
  };

  if (loading) return <SettingsPageShell title="App Details"><Loader /></SettingsPageShell>;
  if (!client) return <SettingsPageShell title="App Details"><p className="text-sm text-ink-gray-500">App not found.</p></SettingsPageShell>;

  const allowExternalSubjects = !!(client.policy_prism?.allow_external_subjects);
  const TABS = allowExternalSubjects ? [...BASE_TABS, "Invites"] : BASE_TABS;

  return (
    <SettingsPageShell
      title="Apps"
      backLabel="Apps"
      onBack={() => navigate(ROUTES.SETTINGS.APPS)}
    >
      <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-8">
      {/* App identity row */}
      <div className="flex items-center gap-3 mb-6">
        <div>
          <p className="text-base font-semibold text-ink-ink dark:text-ink-white leading-tight">{client.name}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <TagPill label={client.client_type} />
            {client.sandbox && <TagPill label="Sandbox" />}
          </div>
        </div>
      </div>

      {/* ATOM corrupt warning */}
      {client.atom_error === "client_atom_corrupt" && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-edge-red-300 bg-fill-red-50 dark:bg-fill-red-600/10 dark:border-edge-red-600/40 px-4 py-3">
          <span className="text-sm text-ink-red-600 dark:text-ink-red-300 font-medium">
            CLIENT_ATOM is corrupt or unreadable. Policy and scopes cannot be displayed. Rotate credentials to issue a new ATOM.
          </span>
        </div>
      )}

      {/* Tabs + Edit button on same row */}
      <div className="flex items-end justify-between border-b border-edge-gray-200 dark:border-edge-panel-border mb-6">
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm transition-colors ${
                activeTab === tab
                  ? "text-ink-primary border-b-2 border-edge-primary font-medium"
                  : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-ink dark:hover:text-ink-gray-200"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        {(activeTab === "Overview" || activeTab === "Config") && client.status !== "revoked" && (
          <div className="flex gap-2 pb-2">
            {editing ? (
              <>
                <SecondaryButton onClick={cancelEdit} disabled={saving} className="min-w-[100px]">Cancel</SecondaryButton>
                <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(editForm, originalEditForm)} className="min-w-[120px]">
                  {saving ? "Saving…" : "Save Changes"}
                </PrimaryButton>
              </>
            ) : (
              <SecondaryButton onClick={startEdit} className="min-w-[100px]">Edit</SecondaryButton>
            )}
          </div>
        )}
      </div>

      {/* Tab content */}
      {activeTab === "Overview" && !editing && (
        <div className="space-y-4">
          <Field label="Client ID" value={client.client_id} />
          <Field label="Name" value={client.name} />
          <Field label="Description" value={client.description} />
          <Field label="Auth Method" value={client.client_auth_method} />
          <Field label="CLIENT_ATOM Birthmark" value={client.client_atom_birthmark || "—"} />
          <Field label="Created By" value={client.created_by_email || client.created_by} />
          <Field label="Created At" value={client.created_at ? new Date(client.created_at).toLocaleString() : "—"} />
          <Field label="Owner Team" value={client.owner_team} />
          <Field label="Owner Contact" value={client.owner_contact} />
          {client.docs_url ? (
            <div>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-0.5">Docs</p>
              <a
                href={client.docs_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-ink-primary hover:underline break-all"
              >
                {client.docs_url}
              </a>
            </div>
          ) : (
            <Field label="Docs" value="" />
          )}
        </div>
      )}

      {activeTab === "Overview" && editing && editForm && (
        <div className="space-y-4">
          <Field label="Client ID" value={client.client_id} />
          <EditField
            label="Name *"
            value={editForm.name}
            onChange={(v) => setEditForm((f) => ({ ...f, name: v }))}
            placeholder="App display name"
          />
          <EditField
            label="Description *"
            value={editForm.description}
            onChange={(v) => setEditForm((f) => ({ ...f, description: v }))}
            placeholder="What does this app do?"
            multiline
          />
          <Field label="Auth Method" value={client.client_auth_method} />
          <Field label="CLIENT_ATOM Birthmark" value={client.client_atom_birthmark || "—"} />
          <Field label="Created By" value={client.created_by_email || client.created_by} />
          <Field label="Created At" value={client.created_at ? new Date(client.created_at).toLocaleString() : "—"} />
          <EditField
            label="Owner Team"
            value={editForm.owner_team}
            onChange={(v) => setEditForm((f) => ({ ...f, owner_team: v }))}
            placeholder="e.g. Platform Engineering"
          />
          <EditField
            label="Owner Contact"
            value={editForm.owner_contact}
            onChange={(v) => setEditForm((f) => ({ ...f, owner_contact: v }))}
            placeholder="e.g. owner@example.com"
          />
          <EditField
            label="Docs URL"
            value={editForm.docs_url}
            onChange={(v) => setEditForm((f) => ({ ...f, docs_url: v }))}
            placeholder="https://..."
          />
        </div>
      )}

      {activeTab === "Config" && !editing && (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-1">Scopes</p>
            <div className="flex flex-wrap gap-2">
              {(client.policy_prism?.scopes || []).map((scope) => (
                <span
                  key={scope}
                  className="text-xs bg-fill-hex-ede9fe text-ink-violet-600 dark:bg-fill-violet-600/20 dark:text-ink-hex-c4b5fd px-2 py-0.5 rounded-full"
                >
                  {scope}
                </span>
              ))}
            </div>
          </div>
          <Field
            label="Allowed Subject Domains"
            value={(client.policy_prism?.allowed_subject_domains || []).join(", ") || "All domains"}
          />
          <Field
            label="Allow External Subjects"
            value={client.policy_prism?.allow_external_subjects ? "Yes" : "No"}
          />
          <Field
            label="Default Group"
            value={
              client.default_group_id
                ? ((allGroups || []).find((g) => g.group_id === client.default_group_id)?.display_name || client.default_group_id)
                : "None"
            }
          />
          {(client.client_type === "device" || client.client_type === "device_gateway") && (
            <div>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-0.5">Signing Key (Public)</p>
              {client.signing_key_public ? (
                <pre className="text-xs font-mono bg-surface-muted-raised dark:bg-surface-muted-raised rounded-lg px-3 py-2 overflow-x-auto text-ink-ink dark:text-ink-gray-200 whitespace-pre-wrap break-all">
                  {client.signing_key_public}
                </pre>
              ) : (
                <p className="text-sm text-ink-red-600 dark:text-ink-red-300">Not set — device authentication will fail. Rotate credentials to set a signing key.</p>
              )}
            </div>
          )}
          {client.client_type === "chat" && (
            <>
              <Field
                label="Chat Platform"
                value={CHAT_PLATFORM_LABELS[client.chat_platform] || client.chat_platform || "—"}
              />
              <Field label="Auth Method" value={client.client_auth_method || "—"} />
              <Field
                label="Allowed Channels"
                value={(client.policy_prism?.allowed_channels || []).join(", ") || "All channels"}
              />
              <Field
                label="Allowed Workspaces"
                value={(client.policy_prism?.allowed_workspaces || []).join(", ") || "All workspaces"}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "Config" && editing && editForm && (
        <div className="space-y-4">
          <div>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-1">Scopes *</p>
            <MultiSelect
              value={editForm.scopes}
              onChange={(v) => setEditForm((f) => ({ ...f, scopes: v }))}
              options={AVAILABLE_SCOPES}
              placeholder="— Select scopes —"
              error={editForm.scopes.length === 0}
            />
          </div>
          <EditField
            label="Allowed Subject Domains"
            value={editForm.allowed_subject_domains}
            onChange={(v) => setEditForm((f) => ({ ...f, allowed_subject_domains: v }))}
            placeholder="e.g. acme.com, partner.io (comma-separated, blank = all)"
          />
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Allow External Subjects</p>
              <p className="text-xs text-ink-gray-400 mt-0.5">Permit users outside your organisation to authenticate as subjects via invite links.</p>
            </div>
            <button
              type="button"
              onClick={() => setEditForm((f) => ({ ...f, allow_external_subjects: !f.allow_external_subjects }))}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                editForm.allow_external_subjects ? "bg-fill-primary" : "bg-fill-gray-300 dark:bg-fill-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-fill-white shadow transition duration-200 ${
                  editForm.allow_external_subjects ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          <div>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-0.5">Default Group <span className="font-normal">(optional)</span></p>
            <Select
              value={editForm.default_group_id || ""}
              onChange={(v) => setEditForm((f) => ({ ...f, default_group_id: v }))}
              placeholder="— No default group —"
              options={[
                { value: "", label: "— No default group —" },
                ...(allGroups || []).filter((g) => g.source === "photon_native").map((g) => ({
                  value: g.group_id,
                  label: g.display_name || g.group_id,
                })),
              ]}
            />
            <p className="mt-1 text-xs text-ink-gray-400">New subjects connecting through this app will be auto-assigned to this group.</p>
          </div>
          {client.client_type === "chat" && (
            <>
              {["slack", "webex", "whatsapp", "telegram"].includes(client.chat_platform) && (
                <div>
                  <EditField
                    label="Webhook Signing Secret"
                    value={editForm.webhook_signing_secret}
                    onChange={(v) => setEditForm((f) => ({ ...f, webhook_signing_secret: v }))}
                    placeholder="Enter new secret to rotate (leave blank to keep current)"
                    type="password"
                  />
                  <p className="-mt-3 text-xs text-ink-gray-400">Write-only — current value is not displayed. Leave blank to keep the existing secret.</p>
                </div>
              )}
              {client.chat_platform === "teams" && (
                <div>
                  <EditField
                    label="Teams Bot App ID"
                    value={editForm.teams_bot_app_id}
                    onChange={(v) => setEditForm((f) => ({ ...f, teams_bot_app_id: v }))}
                    placeholder="Microsoft App ID from your bot registration"
                  />
                  <p className="-mt-3 text-xs text-ink-gray-400">Used to bind Microsoft request tokens to this bot.</p>
                </div>
              )}
              <EditField
                label="Allowed Channels"
                value={editForm.allowed_channels}
                onChange={(v) => setEditForm((f) => ({ ...f, allowed_channels: v }))}
                placeholder="e.g. C1234567890, general — leave blank to allow all channels"
              />
              <p className="-mt-3 text-xs text-ink-gray-400">Comma-separated channel IDs or names. Leave blank to allow any channel.</p>
              <EditField
                label="Allowed Workspaces"
                value={editForm.allowed_workspaces}
                onChange={(v) => setEditForm((f) => ({ ...f, allowed_workspaces: v }))}
                placeholder="e.g. T1234567890, myworkspace — leave blank to allow all workspaces"
              />
              <p className="-mt-3 text-xs text-ink-gray-400">Comma-separated workspace IDs or names. Leave blank to allow any workspace.</p>
            </>
          )}
        </div>
      )}

      {activeTab === "Credentials" && (
        <div className="space-y-4">
          <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">
            Rotate credentials to issue a new client secret and CLIENT_ATOM birthmark.
            Existing JWTs are rejected on their next request.
          </p>

          {newSecret && (
            <div className="space-y-2 p-4 rounded-xl border border-edge-yellow-600 bg-fill-hex-fef9c3 dark:bg-fill-yellow-600/10">
              <p className="text-xs font-semibold text-ink-yellow-600">New secret — save this now, it will not be shown again.</p>
              <code className="block text-xs break-all text-ink-ink dark:text-ink-hex-fde047">{newSecret}</code>
            </div>
          )}

          <SecondaryButton onClick={handleRotate} disabled={rotating || client.status === "revoked"}>
            {rotating ? "Rotating…" : "Rotate Credentials"}
          </SecondaryButton>

          <div className="pt-4 border-t border-edge-gray-200 dark:border-edge-panel-border space-y-4">
            <p className="text-sm font-medium text-ink-red-600">Danger Zone</p>

            {/* Revoke — only when active */}
            {client.status !== "revoked" && (
              <div>
                <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                  Revoking blocks new authentication and rejects existing JWTs on their next request.
                </p>
                {!confirmRevoke ? (
                  <SecondaryButton onClick={() => setConfirmRevoke(true)}>Revoke App</SecondaryButton>
                ) : (
                  <div className="flex gap-3">
                    <PrimaryButton onClick={handleRevoke} disabled={revoking}>
                      {revoking ? "Revoking…" : "Confirm Revoke"}
                    </PrimaryButton>
                    <SecondaryButton onClick={() => setConfirmRevoke(false)}>Cancel</SecondaryButton>
                  </div>
                )}
              </div>
            )}

            {/* Enable — only when revoked */}
            {client.status === "revoked" && (
              <div>
                <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                  Re-enable this app to allow authentication again.
                </p>
                <SecondaryButton onClick={handleEnable} disabled={enabling}>
                  {enabling ? "Enabling…" : "Enable App"}
                </SecondaryButton>
              </div>
            )}

            {/* Delete — only available once revoked */}
            {client.status === "revoked" && (
              <div>
                <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2">
                  Permanently deletes the app, all subjects, credentials and audit history. Irreversible.
                </p>
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="rounded-lg py-[9px] px-[14px] text-sm font-semibold text-ink-on-color bg-fill-red-600 hover:bg-fill-red-700 transition-colors"
                  >
                    Delete App
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-ink-red-600 dark:text-ink-red-300">
                      Type <span className="font-semibold">{client.name}</span> to confirm permanent deletion.
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder={client.name}
                      className="photon-field photon-field-invalid w-full text-sm rounded-lg border border-edge-red-300 dark:border-edge-red-600/50 bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-red-600/40"
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={handleDelete}
                        disabled={deleting || deleteConfirmText !== client.name}
                        className="rounded-lg py-[9px] px-[14px] text-sm font-semibold text-ink-on-color bg-fill-red-600 hover:bg-fill-red-700 disabled:opacity-40 transition-colors"
                      >
                        {deleting ? "Deleting…" : "Confirm Delete"}
                      </button>
                      <SecondaryButton onClick={() => { setConfirmDelete(false); setDeleteConfirmText(""); }}>Cancel</SecondaryButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invites tab */}
      {activeTab === "Invites" && (
        <div className="space-y-5">
          {/* Create invite */}
          <div>
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-2">
              Invite an external subject by email. Invites expire after 30 days.
            </p>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="subject@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateInvite()}
                className="photon-field flex-1 text-sm rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-focus-primary"
              />
              <PrimaryButton onClick={handleCreateInvite} disabled={creatingInvite || !inviteEmail.trim()}>
                {creatingInvite ? "Inviting…" : "Invite"}
              </PrimaryButton>
            </div>
          </div>

          {/* Invite list */}
          {invites === null ? (
            <Loader />
          ) : invites.length === 0 ? (
            <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">No pending invites.</p>
          ) : (
            <div className="divide-y divide-edge-gray-200 dark:divide-edge-panel-border">
              {invites.map((inv) => {
                const displayStatus = getInviteDisplayStatus(inv);
                const statusLabel = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
                return (
                  <div key={inv.invite_id} className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-ink-ink dark:text-ink-gray-200">{inv.email}</p>
                      <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                        {statusLabel}
                        {inv.expires_at ? ` · ${displayStatus === "expired" ? "Expired" : "Expires"} ${new Date(inv.expires_at).toLocaleDateString()}` : ""}
                      </p>
                    </div>
                    {inv.status === "pending" && (
                      <SecondaryButton
                        onClick={() => handleRevokeInvite(inv.invite_id)}
                        disabled={revokingInvite === inv.invite_id}
                      >
                        {revokingInvite === inv.invite_id ? "Revoking…" : "Revoke"}
                      </SecondaryButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Subjects tab */}
      {activeTab === "Subjects" && (
        <div className="space-y-3">
          {subjects === null ? (
            <Loader />
          ) : subjects.length === 0 ? (
            <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400">No subjects have connected yet.</p>
          ) : (
            <div className="divide-y divide-edge-gray-200 dark:divide-edge-panel-border">
              {subjects.map((s) => {
                const isDevice = client.client_type === "device" || client.client_type === "device_gateway";
                const isActive = s.status !== "tombstoned";
                const isConfirmingDecommission = decommissionConfirm === s.link_id;
                return (
                  <div key={s.link_id} className="flex items-start justify-between py-3 gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-ink-ink dark:text-ink-gray-200">
                        {isDevice ? (s.client_subject_id || s.link_id.split(":").slice(1).join(":")) : (s.subject_email || s.link_id)}
                      </p>
                      <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                        {s.status === "tombstoned" ? (isDevice ? "Decommissioned" : "Tombstoned") : "Active"}
                        {!isDevice && s.identity_confidence ? ` · ${s.identity_confidence === "high" ? "High confidence" : "Medium confidence"}` : ""}
                        {s.first_seen_at ? ` · First seen ${new Date(s.first_seen_at).toLocaleDateString()}` : ""}
                      </p>
                      {s.client_subject_atom_birthmark && (
                        <p className="text-xs text-ink-gray-400 dark:text-ink-gray-500 font-mono mt-0.5">
                          Birthmark: {s.client_subject_atom_birthmark}
                        </p>
                      )}
                      {/* Phase 4 — group assignments */}
                      {!isDevice && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {(s.group_assignments || []).map((gId) => {
                            const g = (allGroups || []).find((x) => x.group_id === gId);
                            const label = g?.display_name || gId;
                            const isRemoving =
                              subjectGroupRemoving?.link_id === s.link_id &&
                              subjectGroupRemoving?.group_id === gId;
                            return (
                              <span
                                key={gId}
                                className="inline-flex items-center gap-0.5 text-[10px] bg-fill-blue-50 dark:bg-fill-gradient-primary/15 text-ink-gradient-primary dark:text-ink-blue-300 px-1.5 py-0.5 rounded-full font-medium"
                              >
                                <span className="max-w-[80px] truncate" title={label}>{label}</span>
                                {isActive && (
                                  <button
                                    onClick={() => handleSubjectGroupRemove(s.link_id, gId, s.photon_user_id)}
                                    disabled={isRemoving}
                                    className="ml-0.5 leading-none text-ink-gradient-primary/50 hover:text-ink-red-500 transition-colors disabled:opacity-40"
                                    title="Remove from group"
                                  >
                                    {isRemoving ? "…" : "×"}
                                  </button>
                                )}
                              </span>
                            );
                          })}
                          {isActive && (
                            <div className="relative" data-subject-add-dropdown>
                              <button
                                data-subject-add-dropdown
                                onClick={() => {
                                  setSubjectAddOpen(subjectAddOpen === s.link_id ? null : s.link_id);
                                  setSubjectAddQuery("");
                                }}
                                className="text-[10px] text-ink-gradient-primary hover:underline leading-none"
                              >
                                + Group
                              </button>
                              {subjectAddOpen === s.link_id && (
                                <div
                                  data-subject-add-dropdown
                                  className="absolute top-5 left-0 z-50 w-52 bg-surface-card dark:bg-surface-card border border-edge-gray-200 dark:border-edge-panel-border rounded-xl shadow-lg overflow-hidden"
                                >
                                  <div className="p-2 border-b border-edge-gray-200 dark:border-edge-panel-border">
                                    <input
                                      autoFocus
                                      value={subjectAddQuery}
                                      onChange={(e) => setSubjectAddQuery(e.target.value)}
                                      placeholder="Search groups…"
                                      className="photon-field w-full text-xs bg-fill-navigation-hover dark:bg-fill-navigation-surface-dark rounded-lg px-2 py-1.5 outline-none text-ink-ink dark:text-ink-white placeholder-ink-gray-400"
                                    />
                                  </div>
                                  <div className="max-h-44 overflow-y-auto">
                                    {(() => {
                                      const assigned = new Set(s.group_assignments || []);
                                      const candidates = (allGroups || []).filter(
                                        (g) =>
                                          g.source === "photon_native" &&
                                          !assigned.has(g.group_id) &&
                                          (!subjectAddQuery ||
                                            (g.display_name || "")
                                              .toLowerCase()
                                              .includes(subjectAddQuery.toLowerCase()))
                                      );
                                      if (candidates.length === 0) {
                                        return (
                                          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 p-3">
                                            {subjectAddQuery ? "No groups match." : "No available groups."}
                                          </p>
                                        );
                                      }
                                      return candidates.map((g) => {
                                        const isAdding =
                                          subjectGroupAdding?.link_id === s.link_id &&
                                          subjectGroupAdding?.group_id === g.group_id;
                                        return (
                                          <button
                                            key={g.group_id}
                                            onClick={() =>
                                              handleSubjectGroupAdd(s.link_id, g.group_id, s.photon_user_id)
                                            }
                                            disabled={isAdding}
                                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-fill-navigation-hover dark:hover:bg-fill-navigation-surface-dark text-left transition-colors disabled:opacity-50"
                                          >
                                            <div className="w-4 h-4 rounded-full bg-fill-add-surface dark:bg-fill-gradient-primary/20 flex items-center justify-center flex-shrink-0">
                                              <span className="text-[8px] font-bold text-ink-gradient-primary">G</span>
                                            </div>
                                            <span className="text-xs text-ink-ink dark:text-ink-white flex-1 truncate">
                                              {g.display_name || g.group_id}
                                            </span>
                                            {isAdding && <span className="text-xs text-ink-gray-400">…</span>}
                                          </button>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <div className="flex-shrink-0">
                        {isDevice ? (
                          isConfirmingDecommission ? (
                            <div className="flex flex-col gap-2 items-end">
                              <div className="flex flex-col gap-1.5 w-full">
                                <select
                                  value={decommissionReason}
                                  onChange={(e) => setDecommissionReason(e.target.value)}
                                  disabled={decommissioning === s.link_id}
                                  className="photon-field text-xs rounded-lg px-2 py-1.5 bg-fill-navigation-hover dark:bg-fill-navigation-surface-dark border border-edge-gray-200 dark:border-edge-panel-border text-ink-ink dark:text-ink-white outline-none disabled:opacity-40"
                                >
                                  <option value="admin_action">Admin action</option>
                                  <option value="hardware_retired">Hardware retired</option>
                                  <option value="security_incident">Security incident</option>
                                </select>
                                <label className="flex items-center gap-1.5 text-xs text-ink-gray-500 dark:text-ink-gray-400 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={decommissionPurge}
                                    onChange={(e) => setDecommissionPurge(e.target.checked)}
                                    disabled={decommissioning === s.link_id}
                                    className="rounded disabled:opacity-40"
                                  />
                                  Purge content
                                </label>
                              </div>
                              <div className="flex gap-2 items-center">
                                <button
                                  onClick={() => handleDecommission(s.link_id)}
                                  disabled={decommissioning === s.link_id}
                                  className="rounded-lg py-1.5 px-3 text-xs font-semibold text-ink-on-color bg-fill-red-600 hover:bg-fill-red-700 disabled:opacity-40 transition-colors"
                                >
                                  {decommissioning === s.link_id ? "Decommissioning…" : "Confirm"}
                                </button>
                                <SecondaryButton onClick={() => { setDecommissionConfirm(null); setDecommissionReason("admin_action"); setDecommissionPurge(false); }}>
                                  Cancel
                                </SecondaryButton>
                              </div>
                            </div>
                          ) : (
                            <SecondaryButton
                              onClick={() => setDecommissionConfirm(s.link_id)}
                              disabled={!!decommissioning}
                            >
                              Decommission
                            </SecondaryButton>
                          )
                        ) : (
                          <SecondaryButton
                            onClick={() => handleTombstone(s.link_id)}
                            disabled={tombstoning === s.link_id}
                          >
                            {tombstoning === s.link_id ? "Tombstoning…" : "Tombstone"}
                          </SecondaryButton>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      </div>
    </SettingsPageShell>
  );
};

export default AppDetailPage;
